import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { onTourStart, setTourMenuOpen } from "@/lib/tour";
import BookingPartnerLogo from "@/components/yts/BookingPartnerLogo";

/**
 * A visitor-paced preview of the actual site. Steps only advance when the
 * visitor presses Next (or Back); there is NO autoplay. Exit is available on
 * every step, and the tour can be replayed later from the menu/footer.
 */
const STEPS = [
  {
    path: "/",
    title: { en: "Welcome to Ya Tú Sabes PR", es: "Bienvenido a Ya Tú Sabes PR" },
    text: {
      en: "A local guide to Puerto Rico. Search, explore, save and book at your own pace.",
      es: "Tu guía local de Puerto Rico. Busca, explora, guarda y reserva a tu ritmo.",
    },
  },
  {
    path: "/",
    target: "categories",
    title: { en: "Find your thing", es: "Encuentra tu plan" },
    text: {
      en: "Search or browse food, culture, shops and experiences. Choose any category whenever you want.",
      es: "Busca comida, cultura, tiendas y experiencias. Elige cualquier categoría cuando quieras.",
    },
  },
  {
    path: "/",
    target: "bookable",
    showPartners: true,
    title: { en: "Find an experience to book", es: "Encuentra qué reservar" },
    text: {
      en: "Explore real activities in our booking carousel. Viator and FareHarbor show which platform manages availability and checkout.",
      es: "Explora actividades reales en nuestro carrusel. Viator y FareHarbor indican qué plataforma gestiona disponibilidad y pagos.",
    },
  },
  {
    path: "/",
    showMenu: true,
    title: { en: "Everything else is in the menu", es: "Todo lo demás está en el menú" },
    text: {
      en: "Find hotels, events, jobs, neighborhood guides and language settings without crowding the homepage.",
      es: "Encuentra hoteles, eventos, empleos, guías de barrios y el idioma sin llenar la página principal.",
    },
  },
  {
    path: "/map?tour=1",
    title: { en: "Explore the island map", es: "Explora el mapa de la isla" },
    text: {
      en: "Use the real pins and filters to find places throughout Puerto Rico.",
      es: "Usa los pines y filtros reales para encontrar lugares por todo Puerto Rico.",
    },
  },
  {
    path: "/districts",
    title: { en: "Explore neighborhood by neighborhood", es: "Explora barrio por barrio" },
    text: {
      en: "Find the streets, businesses and local neighborhoods behind the guide.",
      es: "Conoce las calles, comercios y barrios locales de la guía.",
    },
  },
  {
    path: "/magazine",
    title: { en: "Read the stories", es: "Lee las historias" },
    text: {
      en: "See the island through historical photographs, places and community stories.",
      es: "Conoce la isla a través de fotografías históricas, lugares e historias de la comunidad.",
    },
  },
  {
    path: "/saved",
    title: { en: "Save and plan your day", es: "Guarda y planifica tu día" },
    text: {
      en: "Keep favorite places in one list. Plan and share a day without signing up.",
      es: "Guarda tus lugares favoritos, prepara tu día y comparte sin registrarte.",
    },
  },
  {
    path: "/",
    title: { en: "You're ready to explore", es: "Ya puedes explorar" },
    text: {
      en: "The homepage is yours. Explore at your own pace or replay this tour from the menu.",
      es: "La guía es tuya. Explora a tu ritmo o repite el recorrido desde el menú.",
    },
  },
];

function scrollToTourTarget(name) {
  if (!name) return;
  const target = Array.from(document.querySelectorAll(`[data-tour="${name}"]`)).find((el) => {
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  });
  target?.scrollIntoView({ block: "center", behavior: "smooth" });
}

export default function SiteTour() {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const navigate = useNavigate();
  const { lang } = useLang();
  const es = lang === "es";

  const finish = useCallback(() => {
    setTourMenuOpen(false);
    setOpen(false);
    setIndex(0);
    // A tour should never strand a newcomer in a subpage or leave a map
    // demo query flag in the URL. Keep browser Back history clean as well.
    navigate("/", { replace: true });
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [navigate]);

  useEffect(() => onTourStart(() => {
    setIndex(0);
    setOpen(true);
  }), []);

  useEffect(() => {
    if (!open) return;
    const step = STEPS[index];
    // Closing the real menu comes before navigating to the real map.
    setTourMenuOpen(false);
    navigate(step.path, { replace: true });
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    const actionTimer = window.setTimeout(() => {
      if (step.showMenu) setTourMenuOpen(true);
      if (step.target) scrollToTourTarget(step.target);
    }, step.showMenu ? 180 : 350);
    return () => window.clearTimeout(actionTimer);
  }, [open, index, navigate]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event) => {
      if (event.key === "Escape") finish();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, finish]);

  if (!open) return null;

  const step = STEPS[index];
  const last = index === STEPS.length - 1;
  const title = es ? step.title.es : step.title.en;
  const description = es ? step.text.es : step.text.en;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[1201] flex justify-center px-3 pb-[max(12px,env(safe-area-inset-bottom))]">
      <div
        role="dialog"
        aria-label={es ? "Tour del sitio" : "Site tour"}
        className="pointer-events-auto w-full max-w-[420px] rounded-[18px] border border-white/[0.13] bg-[#171614] p-4 text-[#f2efe9] shadow-[0_26px_60px_-18px_rgba(0,0,0,0.72)]"
      >
        <div className="flex items-center justify-between gap-3">
          <p className="text-[9px] font-black uppercase tracking-[0.22em] text-[#a9a49a]">
            {es ? "Conoce el sitio" : "Site preview"} · {index + 1}/{STEPS.length}
          </p>
          <button
            type="button"
            onClick={finish}
            aria-label={es ? "Salir del recorrido" : "Exit tour"}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-white/40 bg-white/10 text-white transition hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ef4036]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-2 h-[3px] w-full overflow-hidden rounded-full bg-white/[0.14]">
          <div
            className="h-full rounded-full bg-[#ef4036] transition-[width] duration-500 ease-out"
            style={{ width: `${((index + 1) / STEPS.length) * 100}%` }}
          />
        </div>
        <h2 className="mt-3 text-[19px] font-extrabold leading-[1.12] tracking-[-0.04em]">{title}</h2>
        <p className="mt-1.5 text-[12.5px] leading-[1.55] text-[#c5c1b8]">{description}</p>
        {step.showPartners && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="flex min-h-9 items-center rounded-[3px] bg-white px-3"><BookingPartnerLogo provider="viator" /></span>
            <span className="flex min-h-9 items-center rounded-[3px] bg-white px-3"><BookingPartnerLogo provider="fareharbor" /></span>
          </div>
        )}
        <div className="mt-3 flex items-center justify-between gap-2">
          <span className="text-[10px] font-medium text-[#a9a49a]">
            {es ? "Tú decides cuándo avanzar" : "You control the pace"}
          </span>
          <span className="text-[10px] font-semibold text-[#a9a49a]">
            {es ? "Siguiente →" : "Tap Next →"}
          </span>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setIndex((current) => Math.max(0, current - 1))}
            disabled={index === 0}
            aria-label={es ? "Anterior" : "Back"}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-[9px] border border-white/30 text-[#e8e4dc] transition enabled:hover:bg-white/[0.08] disabled:opacity-35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ef4036]"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => (last ? finish() : setIndex((current) => current + 1))}
            className="inline-flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-[9px] bg-[#f2efe9] px-4 text-[11px] font-black uppercase tracking-[0.12em] text-[#171614] transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ef4036]"
          >
            {last ? (es ? "Empezar a explorar" : "Start exploring") : es ? "Siguiente" : "Next"}
            {!last && <ArrowRight className="h-4 w-4" />}
          </button>
          {!last && (
            <button
              type="button"
              onClick={finish}
              className="h-[42px] shrink-0 px-2 text-[11px] font-bold text-[#a9a49a] transition hover:text-white"
            >
              {es ? "Salir" : "Skip"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
